import { supabase } from './supabaseClient';
import { projectId, publicAnonKey } from '../utils/supabase/info';

export type TranscribeWhisperResult =
  | { ok: true; text: string }
  | { ok: false; error: string };

/**
 * Edge Function transcribe-audio へ録音 Blob を送り、Whisper のテキストを返す。
 */
export async function transcribeWhisperAudio(
  audioBlob: Blob,
  prompt: string
): Promise<TranscribeWhisperResult> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session?.access_token) {
    return { ok: false, error: 'ログインセッションがありません。再度ログインしてください。' };
  }

  const form = new FormData();
  const ext = audioBlob.type.includes('webm') ? 'webm' : audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
  form.append('file', audioBlob, `recording.${ext}`);
  if (prompt) {
    form.append('prompt', prompt);
  }

  const url = `https://${projectId}.supabase.co/functions/v1/transcribe-audio`;
  // Edge Functions へのブラウザ呼び出しでは apikey（anon）が必須。無いとゲートウェイが 502 になりやすい。
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionData.session.access_token}`,
      apikey: publicAnonKey,
    },
    body: form,
  });

  const raw = await res.text();
  let body: unknown;
  if (!raw.trim()) {
    if (res.status === 502) {
      return {
        ok: false,
        error:
          '文字起こしサーバーが応答しませんでした。Supabase の Edge Function「transcribe-audio」のログと、シークレット OPENAI_API_KEY の有無を確認してください。',
      };
    }
    return { ok: false, error: 'サーバーからの応答を解釈できませんでした。' };
  }
  try {
    body = JSON.parse(raw) as unknown;
  } catch {
    return { ok: false, error: 'サーバーからの応答を解釈できませんでした。' };
  }

  if (!res.ok) {
    const msg =
      typeof body === 'object' && body !== null && 'error' in body && typeof (body as { error: string }).error === 'string'
        ? (body as { error: string }).error
        : `文字起こしに失敗しました（${res.status}）`;
    return { ok: false, error: msg };
  }

  if (typeof body === 'object' && body !== null && 'text' in body && typeof (body as { text: string }).text === 'string') {
    return { ok: true, text: (body as { text: string }).text };
  }

  return { ok: false, error: '応答にテキストがありませんでした。' };
}
